import { pageTitle } from '@palaccount/config';
import {
  CHANGE_NUMBER_SEQUENCES,
  NUMBER_SEQUENCE_MESSAGES,
  type GapCheckResult,
  type NumberSequenceResponse,
} from '@palaccount/contracts';
import { Check, Pencil, ScanSearch, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Ltr } from '@/components/amount';
import { ConfirmBox } from '@/components/confirm-box';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { localizeServerCause, localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';

import { AuthorshipLine } from '@/features/master-data/authorship-line';
import { downloadXlsx } from '@/features/reports/export';

import { useCheckGaps, useNumberSequences, useUpdateNumberSequence } from './number-sequences-api';

/**
 * MD-13 NUMBER SEQUENCES — "where the counter that obeys §4.9's rules lives".
 *
 * §1: "You use it once when the business starts, and rarely after that." So it
 * is deliberately plain — §4's own mock-up is a table with no New button,
 * because "the set of sequences is fixed by the set of documents, and one
 * exists for every one of them, created with the company".
 *
 * NO PAGER, and that is a decision rather than an omission: the shared pager
 * exists for lists whose length is a function of how long a company has been
 * trading, and this one's length is a function of how many DOCUMENT TYPES the
 * product has. Thirty-five rows, plus a handful of sub-series (two cheque
 * registers, a row per work-order year). A person comparing prefixes needs to
 * see them all at once — which is the whole reason ERR-MD-13-02 exists.
 *
 * ── WHAT THIS SCREEN IS CAREFUL ABOUT ──────────────────────────────────────
 *
 * It never re-states a rule the server enforces. The refusal for a lowered
 * next number is shown exactly as the DATABASE wrote it (ERR-MD-13-01), and
 * the browser does not pre-empt it — a client-side "must be higher" check
 * would be a second copy of a rule with one enforcement point, and the copy is
 * always the one that drifts.
 *
 * It never offers to REPAIR a gap. §12 names the stake — "a gap in an invoice
 * sequence is treated by many tax authorities as a missing invoice, and asked
 * about" — and the honest answer is a report and a sentence about what a gap
 * can legitimately be, never a button that renumbers documents.
 *
 * And it draws §7's three states as three states. Locked is grey and real:
 * the allocator holds a row lock while somebody confirms a document, so a
 * counter genuinely can be unavailable for a moment, and saying so beats a
 * button that hangs. It does NOT grey the row's Edit button for it — a lock is
 * held for the length of one save, so a button disabled from a snapshot taken
 * when the page loaded would be wrong within milliseconds. 16.7 row 3's
 * treatment of a rule-or-state block is the right one: the control stays live
 * and the refusal, if it still applies, comes from the server.
 */

/** §7's three states, with §7's own colours. */
function statusClass(status: NumberSequenceResponse['status']): string {
  switch (status) {
    case 'gap_found':
      return 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300';
    case 'locked':
      return 'bg-muted text-muted-foreground';
    default:
      return 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300';
  }
}

interface Draft {
  prefix: string;
  padWidth: string;
  nextNo: string;
}

/**
 * THE QUESTION THAT IS STANDING, AND THE THREE VALUES IT WAS ASKED ABOUT.
 *
 * The values are FROZEN into this record when the question opens rather than
 * re-read from `draft` when the dialog renders, and that is the whole reason
 * the shape exists. `preview` is the sentence's promise — "the next sales
 * invoice will be SI-2043" — and `prefix`/`padWidth`/`nextNo` are what the
 * PATCH will carry. Recomputing the promise at render time from the same state
 * the inputs are still bound to would let the two come apart: the box is modal
 * in a real browser, but jsdom's fallback is not, and neither is a caller who
 * later adds a keyboard shortcut. A dialog whose sentence can disagree with
 * its own write is a worse failure than no dialog, so the disagreement is made
 * impossible rather than unlikely.
 */
interface SaveAsk {
  row: NumberSequenceResponse;
  prefix: string;
  padWidth: number;
  nextNo: number;
  /** The CODE the next document will print — §6's consequence, not a field. */
  preview: string;
}

export function NumberSequencesPage() {
  const { t } = useTranslation();
  const { data, isPending } = useNumberSequences();
  const update = useUpdateNumberSequence();
  const checkGaps = useCheckGaps();

  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>({ prefix: '', padWidth: '', nextNo: '' });
  const [error, setError] = useState<string | null>(null);
  const [gaps, setGaps] = useState<GapCheckResult | null>(null);
  /**
   * MD-13 §6's "strong confirmation naming what will change" — ADM-02 §8's
   * SHARED BOX now. This was the LAST `window.confirm` call site in the
   * product, deferred twice: once by the dialogs sweep to another wave's fence
   * (that wave has since merged), and once by the sweep after it.
   *
   * BR-PERM-15, spec line 35168: "Every irreversible action must use one of
   * these two patterns. A new pattern must not be invented." `window.confirm`
   * is NEITHER — browser chrome with an OK button, no padlock, no amber tile,
   * no 420px panel the product controls, and NO TRANSLATION, which on this
   * screen was the sharpest edge of the three: the sentence it displayed was
   * assembled from two dictionary keys that both have Arabic, and then handed
   * to a dialog whose own Cancel and OK buttons are whatever the browser's
   * locale says. Half a translated question.
   *
   * ── THE ACT IS IRREVERSIBLE, ESTABLISHED AND NOT ASSUMED (GEN-205) ────────
   *
   * The undone clause is licensed by the DATABASE, not by this screen's sense
   * of gravity. `number_sequences_forward_only()` (migration 20260819110000,
   * line 283) refuses `NEW.next_no < OLD.next_no` outright and raises
   * ERR-MD-13-01, so a counter raised here can NEVER be lowered again: there
   * is no un-save route on `numberSequencesContract`, no service path, and the
   * only other writer of the column is the allocator, which adds. The same
   * trigger fixes `start_no` (BR-MD-13-09) and refuses the DELETE
   * (BR-MD-13-01). And BR-MD-13-07 keeps every code already issued, which is
   * the OTHER half a person needs before they commit — so the body says both.
   *
   * The clause is already inside `numseq.saveConsequence` in both languages,
   * which is why the body is COMPOSED FROM THE TWO KEYS THAT ALREADY EXIST
   * rather than replaced by a new pair. The count post's split was needed
   * because its one key held a whole question; here the question is the part
   * that was MISSING — there was never a title, because browser chrome does
   * not have one.
   *
   * ── GEN-209 IS DELIBERATELY NOT CITED ─────────────────────────────────────
   *
   * Its roster of seven — deleting a draft, cancelling a confirmed document,
   * switching a master record off, revoking an API key, approving or
   * rejecting a submission, posting a revaluation, resetting a layout — does
   * not reach a settings save, and reading one of those phrases onto this
   * would be an inference. BR-PERM-15 reaches the site without it: the act is
   * irreversible, so it takes the shared box on that ground alone. That is the
   * same treatment the count post took (5642f41), for the same reason.
   *
   * GEN-210 CONSIDERED AND NOT CITED, in place: "an action that destroys money
   * or stock data must additionally name the amount or the count." This
   * destroys neither. What it changes is what the NEXT document will be
   * CALLED, and the body names exactly that — `preview`, the code itself,
   * which is the consequence a person can actually check against their
   * paperwork. A count of affected documents would be zero and misleading.
   */
  const [saveAsk, setSaveAsk] = useState<SaveAsk | null>(null);

  useEffect(() => {
    document.title = pageTitle(t('numseq.title'));
  }, [t]);

  const keyOf = (row: NumberSequenceResponse) => `${row.series} ${row.scope}`;

  const startEdit = (row: NumberSequenceResponse) => {
    setError(null);
    setGaps(null);
    // A question raised over the PREVIOUS row must not survive into this one.
    // It cannot happen through the UI today — the dialog is modal and the row
    // controls sit behind it — but the state is per-page and the row is not,
    // so the pairing is closed here rather than left to depend on the modality
    // (which jsdom does not have, and which no unit test could therefore see).
    setSaveAsk(null);
    setEditing(keyOf(row));
    setDraft({
      prefix: row.prefix,
      padWidth: String(row.padWidth),
      nextNo: String(row.nextNo),
    });
  };

  /**
   * GEN-208, spec line 4707: the Save click OPENS THE QUESTION and does
   * nothing else — nothing reaches the server here, ever. The write lives in
   * `commitSave`, on the dialog's own verb.
   *
   * The two RANGE checks stay on this side of the question, and that is not a
   * GEN-208 breach: they reach no server, they perform no act, and they exist
   * so that a value which cannot possibly save never gets a confirmation
   * offered for it. Asking "the next sales invoice will be SI-00000" about a
   * width of zero would be a dialog promising something the server is about to
   * refuse — the confirmation would be the lie, not the input. The behaviour
   * is unchanged from the chrome version and is pinned by the ERR-MD-13-03
   * test, which asserts the question is never even raised.
   */
  const askSave = (row: NumberSequenceResponse) => {
    const nextNo = Number(draft.nextNo);
    const padWidth = Number(draft.padWidth);
    setError(null);

    // §5 field 3 and ERR-MD-13-03 — the RANGE, which is a shape question and
    // not the forward-only rule. Checking a range here is not a second copy of
    // anything: the server refuses the same values, and this only saves the
    // person a round trip on a typo.
    if (!Number.isInteger(nextNo) || nextNo < 1 || nextNo > 999_999_999) {
      // Through the map, not raw. The SERVER refuses the same two values with
      // the same two sentences, so screen and refusal must say the identical
      // thing in the reader's language — which is what `localizeServerMessage`
      // guarantees and a hand-written key would let drift.
      setError(localizeServerMessage(t, NUMBER_SEQUENCE_MESSAGES.badNextNumber));
      return;
    }
    if (!Number.isInteger(padWidth) || padWidth < 1 || padWidth > 10) {
      setError(localizeServerMessage(t, NUMBER_SEQUENCE_MESSAGES.badWidth));
      return;
    }

    // §6: "Stores the change, after a strong confirmation naming what will
    // change." The confirmation shows the CODE the next document will print,
    // not the number — because "the next sales invoice will be SI-2043" is the
    // consequence and "next number 2043" is a field value.
    //
    // TRIMMED ONCE, HERE, AND USED FOR BOTH. The preview used to be built from
    // the raw `draft.prefix` while the PATCH sent `draft.prefix.trim()`, so a
    // prefix typed with a trailing space was previewed as one code and saved
    // as another. Nobody would have noticed in the chrome dialog and it would
    // still have been the dialog telling a person something untrue about the
    // write it was asking them to authorise. One value now feeds the sentence
    // and the request, so they cannot disagree. (The server trims too, and
    // `PrefixSchema` trims — this makes the SCREEN agree with them rather than
    // adding a third opinion.)
    const prefix = draft.prefix.trim();
    const previewNo = String(nextNo).padStart(padWidth, '0');
    const preview = `${prefix}${previewNo}`;

    setSaveAsk({ row, prefix, padWidth, nextNo, preview });
  };

  /**
   * The dialog's own verb, and the ONLY place this screen writes. Takes the
   * frozen `SaveAsk` rather than re-reading `draft`, so the request carries
   * exactly the three values the sentence was composed from.
   */
  const commitSave = (ask: SaveAsk) => {
    setSaveAsk(null);
    update.mutate(
      {
        series: ask.row.series,
        scope: ask.row.scope,
        prefix: ask.prefix,
        padWidth: ask.padWidth,
        nextNo: ask.nextNo,
      },
      {
        onSuccess: () => setEditing(null),
        // ERR-MD-13-01 / ERR-MD-13-02 / §7's Locked all arrive as 409 with
        // the server's own sentence. Shown as written; the browser adds
        // nothing and interprets nothing.
        //
        // WHAT CHANGED, AND WHY IT DOES NOT WEAKEN THE LINE ABOVE. The old
        // expression was `caught instanceof ApiError ? caught.message :
        // String(caught)`, which flattened a request that NEVER ANSWERED into
        // the browser's own words — an Arabic reader met `Failed to fetch`.
        // `localizeServerCause` keeps the two apart. For the refusal half it
        // is a superset of what was here: `localizeServerMessage` returns the
        // server's sentence UNCHANGED when nothing maps it, so the 409s above
        // still arrive as written, and any of them that later gains a
        // dictionary entry arrives translated instead of in English.
        onError: (caught) => setError(localizeServerCause(t, caught, 'numseq.errSaveFailed')),
      },
    );
  };

  const onCheckGaps = (row: NumberSequenceResponse) => {
    setError(null);
    checkGaps.mutate(
      { series: row.series, scope: row.scope },
      {
        onSuccess: setGaps,
        onError: (caught) => setError(localizeServerCause(t, caught, 'numseq.errSaveFailed')),
      },
    );
  };

  const onExcel = () => {
    const rows = (data?.sequences ?? []).map((row) => [
      row.document,
      row.prefix,
      String(row.padWidth),
      String(row.startNo),
      String(row.nextNo),
      row.lastGiven ?? '',
      String(row.issuedCount),
      String(row.discrepancy),
    ]);
    downloadXlsx(
      'number-sequences.xlsx',
      t('numseq.title'),
      [
        t('numseq.colDocument'),
        t('numseq.colPrefix'),
        t('numseq.colWidth'),
        t('numseq.colStart'),
        t('numseq.colNext'),
        t('numseq.colLastGiven'),
        t('numseq.colIssued'),
        t('numseq.colSkipped'),
      ],
      rows,
    );
  };

  return (
    <div className="grid gap-4">
      <nav className="flex flex-wrap gap-1 border-b pb-px" aria-label={t('shell.navSettings')}>
        <Link
          to="/settings"
          className="rounded-t-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          {t('settings.tabGeneral')}
        </Link>
        <span className="rounded-t-md border-b-2 border-primary px-3 py-2 text-sm font-medium">
          {t('numseq.title')}
        </span>
      </nav>

      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="me-auto text-lg font-bold">{t('numseq.title')}</h2>
        <Button variant="outline" size="sm" onClick={onExcel} disabled={!data}>
          {t('numseq.excel')}
        </Button>
      </div>

      {/* §1's own framing, kept on the screen: this is a place you visit once.
          A settings screen that does not say that invites tinkering with the
          one setting in the product that cannot be undone. */}
      <p className="text-sm text-muted-foreground">{t('numseq.intro')}</p>

      {error ? (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
        >
          {error}
        </div>
      ) : null}

      {gaps ? (
        <Card>
          <CardContent className="grid gap-2 p-4 text-sm">
            <p className="font-medium">
              {t('numseq.gapsFor', { document: gaps.document })}{' '}
              <Ltr value={`${gaps.checkedFrom}–${gaps.checkedTo}`} className="font-mono" />
            </p>
            {gaps.gapCount === 0 ? (
              <p className="text-green-800 dark:text-green-300">{gaps.note}</p>
            ) : (
              <>
                <p className="font-medium text-red-800 dark:text-red-300">
                  {t('numseq.gapCount', { count: gaps.gapCount })}
                </p>
                <ul className="grid gap-1">
                  {gaps.gaps.map((gap) => (
                    <li key={gap.no} className="font-mono text-xs">
                      {gap.message}
                    </li>
                  ))}
                </ul>
                {gaps.truncated ? (
                  <p className="text-xs text-muted-foreground">{t('numseq.gapsTruncated')}</p>
                ) : null}
                {/* The sentence that has to sit beside every gap report: a red
                    chip with no explanation reads as an accusation, and a gap
                    is very often the rules working (GEN-80/-81). */}
                <p className="text-muted-foreground">{gaps.note}</p>
              </>
            )}
            <div>
              <Button variant="ghost" size="sm" onClick={() => setGaps(null)}>
                {t('numseq.dismiss')}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('numseq.colDocument')}</TableHead>
                <TableHead>{t('numseq.colPrefix')}</TableHead>
                <TableHead className="text-end">{t('numseq.colWidth')}</TableHead>
                <TableHead className="text-end">{t('numseq.colStart')}</TableHead>
                <TableHead className="text-end">{t('numseq.colNext')}</TableHead>
                <TableHead>{t('numseq.colLastGiven')}</TableHead>
                <TableHead className="text-end">{t('numseq.colIssued')}</TableHead>
                <TableHead>{t('numseq.colStatus')}</TableHead>
                <TableHead className="text-end">{t('numseq.colActions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPending ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-muted-foreground">
                    {t('common.loading')}
                  </TableCell>
                </TableRow>
              ) : null}
              {(data?.sequences ?? []).map((row) => {
                const key = keyOf(row);
                const isEditing = editing === key;
                return (
                  <TableRow key={key} className={row.status === 'locked' ? 'opacity-70' : ''}>
                    <TableCell className="font-medium">
                      {row.document}
                      {/*
                        MD-13's rows are minted by `app_ensure_number_sequences()`
                        at signup, so `createdBy` here is PERMANENTLY null for
                        every tenant — this screen is the one place the seam is
                        the normal case rather than the historical one. The
                        created DATE is still true and still shown.

                        `updatedAt` on this table moves every time any document
                        is numbered, because `number_sequences_forward_only()`
                        stamps it in the allocator. `updatedBy` does NOT: it is
                        written only by this screen's save. So the NAME is a
                        true answer and the TIMESTAMP beside it is about a
                        different event — which is why this is the one call
                        site that passes `changedAtIsNotAChangeTime`.
                      */}
                      {isEditing ? (
                        <AuthorshipLine record={row} changedAtIsNotAChangeTime />
                      ) : null}
                    </TableCell>

                    <TableCell>
                      {isEditing ? (
                        <Input
                          value={draft.prefix}
                          maxLength={8}
                          aria-label={t('numseq.colPrefix')}
                          onChange={(event) =>
                            setDraft((old) => ({ ...old, prefix: event.target.value }))
                          }
                          className="w-24 font-mono"
                        />
                      ) : (
                        <Ltr value={row.prefix} className="font-mono" />
                      )}
                    </TableCell>

                    <TableCell className="text-end">
                      {isEditing ? (
                        <Input
                          value={draft.padWidth}
                          inputMode="numeric"
                          aria-label={t('numseq.colWidth')}
                          onChange={(event) =>
                            setDraft((old) => ({ ...old, padWidth: event.target.value }))
                          }
                          className="w-16 text-end font-mono"
                        />
                      ) : (
                        <Ltr value={String(row.padWidth)} className="font-mono" />
                      )}
                    </TableCell>

                    <TableCell className="text-end">
                      <Ltr value={row.startNo.toLocaleString()} className="font-mono" />
                    </TableCell>

                    <TableCell className="text-end">
                      {isEditing ? (
                        <Input
                          value={draft.nextNo}
                          inputMode="numeric"
                          aria-label={t('numseq.colNext')}
                          onChange={(event) =>
                            setDraft((old) => ({ ...old, nextNo: event.target.value }))
                          }
                          className="w-28 text-end font-mono"
                        />
                      ) : (
                        <Ltr value={row.nextNo.toLocaleString()} className="font-mono" />
                      )}
                    </TableCell>

                    <TableCell>
                      {/* §5 field 5. A sequence that has issued nothing has no
                          last code, and an em dash says so better than a
                          made-up one would. */}
                      {row.lastGiven === null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <Ltr value={row.lastGiven} className="font-mono" />
                      )}
                    </TableCell>

                    <TableCell className="text-end">
                      <Ltr value={row.issuedCount.toLocaleString()} className="font-mono" />
                    </TableCell>

                    <TableCell>
                      <span
                        className={cn(
                          'inline-block rounded-full px-2 py-0.5 text-xs font-medium',
                          statusClass(row.status),
                        )}
                      >
                        {t(`numseq.status.${row.status}`)}
                      </span>
                      {/* BR-MD-13-09's discrepancy as a NUMBER. "12 numbers
                          were skipped" is a question somebody can answer;
                          "something is wrong" is not. */}
                      {row.discrepancy !== 0 ? (
                        <span className="ms-2 text-xs text-muted-foreground">
                          {t('numseq.skipped', { count: row.discrepancy })}
                        </span>
                      ) : null}
                      {row.isFull ? (
                        <span className="ms-2 text-xs text-red-800 dark:text-red-300">
                          {t('numseq.full')}
                        </span>
                      ) : null}
                    </TableCell>

                    <TableCell className="text-end">
                      <div className="flex justify-end gap-1">
                        {isEditing ? (
                          <>
                            {/* GEN-208: this click OPENS THE QUESTION and does
                                nothing else. The PATCH is `commitSave`, on the
                                dialog's verb, and nowhere else on this
                                screen. */}
                            <Button
                              size="sm"
                              onClick={() => askSave(row)}
                              disabled={update.isPending}
                            >
                              <Check aria-hidden className="size-4" />
                              {t('numseq.save')}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditing(null);
                                setError(null);
                                setSaveAsk(null);
                              }}
                            >
                              <X aria-hidden className="size-4" />
                              {t('common.cancel')}
                            </Button>
                          </>
                        ) : (
                          <>
                            {/* BR-PERM-17/-18: greyed with a reason, never
                                hidden — an Accountant has to be able to see
                                that Edit exists and is not theirs. */}
                            <Gated
                              permission={CHANGE_NUMBER_SEQUENCES}
                              reasonKey="numseq.cannotEdit"
                            >
                              {/* §7's Locked is a STATE, not a permission, so
                                  16.7 row 3 governs it: the button stays LIVE
                                  and explains on click. That is also the only
                                  honest treatment here — the lock is held for
                                  the length of somebody's save, which is
                                  milliseconds, so a button greyed from a
                                  snapshot taken when the page loaded would be
                                  wrong almost immediately. The grey chip says
                                  the state; if the counter is still held when
                                  they press Save, the server says so. */}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => startEdit(row)}
                                title={
                                  row.status === 'locked'
                                    ? localizeServerMessage(t, NUMBER_SEQUENCE_MESSAGES.locked)
                                    : undefined
                                }
                              >
                                <Pencil aria-hidden className="size-4" />
                                {t('numseq.edit')}
                              </Button>
                            </Gated>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => onCheckGaps(row)}
                              disabled={checkGaps.isPending}
                            >
                              <ScanSearch aria-hidden className="size-4" />
                              {t('numseq.checkGaps')}
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/*
        ONE dialog for the whole table, not one per row — the table is
        thirty-five rows deep, and a dialog element nested in every data cell
        would be thirty-five elements in the browser's top layer waiting for a
        state that only ever holds one of them. `saveAsk` names the row it was
        raised for, so the single instance is exact.

        It renders OUTSIDE the table for a second reason: a dialog element is
        not valid content for a table row, so React would place it where the
        markup says while the browser's own parser disagrees.

        (The two paragraphs above name those elements in WORDS rather than in
        angle brackets on purpose. `table-density.test.tsx` scans every file
        under `features/` for a raw cell tag and requires any file containing
        one to be listed in `DENSITY_EXEMPT_TABLES` with a justification — and
        the scan reads BYTES, so it cannot tell a tag written in a comment from
        a tag rendered on a screen. Spelling it out here tripped that guard on
        a screen that renders no raw cell at all. The repair belongs on this
        side: adding this file to the exempt list would have recorded a false
        fact about it and switched the guard off for every future edit.)

        ── THE FOUR SLOTS OF GEN-205, AND WHERE EACH COMES FROM ──────────────

        title   the QUESTION — `numseq.confirmSaveTitle`, the one string this
                change adds. It names the DOCUMENT because this table has an
                Edit button on every one of thirty-five rows, so "I opened the
                wrong row" is the precise mistake a confirmation is here to
                catch, and a static "Change this numbering?" could not catch
                it.
        body    what will happen, AND that it cannot be undone — both already
                shipped, in both languages, so neither is re-minted here. The
                first sentence is the CODE the next document will print; the
                second is BR-MD-13-07, said before the person commits rather
                than discovered afterwards.
        cancel  ConfirmBox's own, which is also Escape and the backdrop
                (GEN-207 — all three mean no, and none of them writes).
        verb    `numseq.save` — GEN-206 wants the verb and this already is
                one, and it is the same word as the button that raised the
                dialog, which is the property §8 is after.

        TWO SENTENCES, TWO MECHANISMS, and the difference is the map's — the
        note that used to sit at the old call site, unchanged because the
        reason is unchanged:

        `saveConsequence` is PARAMETERISED — it composes the preview code and
        the document name — so `STATIC_MESSAGE_KEYS`, which matches WHOLE
        sentences by exact value, has no single string to key on. It is
        addressed as a dictionary key HERE, where both arguments are known.
        Pointing a map entry at this key would render the literal `{code}` and
        `{what}`, because `localizeServerMessage` calls `t(key)` with no
        arguments.

        `prefixKeepsHistory` is FIXED, so it goes through the map like every
        other contract sentence on this screen.

        `what` is `row.document`, the server's own document name — passed
        through untranslated, which is what this screen already does with the
        same value at `numseq.gapsFor` and in the table body. Translating it
        here alone would make one sentence disagree with the row above it, and
        now also with the title directly above it.
      */}
      {saveAsk === null ? null : (
        <ConfirmBox
          open
          title={t('numseq.confirmSaveTitle', {
            what: saveAsk.row.document.toLowerCase(),
          })}
          body={
            <>
              <p>
                {t('numseq.saveConsequence', {
                  code: saveAsk.preview,
                  what: saveAsk.row.document.toLowerCase(),
                })}
              </p>
              <p className="mt-2">
                {localizeServerMessage(t, NUMBER_SEQUENCE_MESSAGES.prefixKeepsHistory)}
              </p>
            </>
          }
          confirmLabel={t('numseq.save')}
          busy={update.isPending}
          onConfirm={() => commitSave(saveAsk)}
          onCancel={() => setSaveAsk(null)}
        />
      )}
    </div>
  );
}
